import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Mode } from "../../types";
import { ChatMessage, Message, Reference } from "../../types/tutor.types";
import { useTutorChatStore } from "../../store/useTutorChatStore";
import { useChatHistoryQuery } from "../queries/useChatHistoryQuery";
import { tutorApiService } from "../../services/tutorApi";
import { useGroupListStore } from "../../store/useGroupListStore";
import { useInfiniteGroupListQuery } from "../queries/useInfiniteGroupListQuery";
import { formatDateString } from "../../../../utils/commonFunctions";
import useChat from "../useChat";
import useTextToSpeech from "../../../../components/speech/useTextToSpeech";
import { stripMarkdown } from "../../utils/stripMarkdown";

/**
 * useChatView
 *
 * 채팅 화면 전용 presentation 훅
 *
 * 기능:
 * - 채팅 히스토리 데이터 페칭
 * - Bubble 컴포넌트용 데이터 변환
 * - 인터랙션 핸들러 제공 (복사, 평가, 재생성, 추천 질문)
 */

interface UseChatViewOptions {
  mode: Mode;
  groupId: string | null;
}

interface BubbleReference {
  title: string;
  source: string;
  url: string;
  summary: string;
  images?: Array<{
    src: string;
    alt: string;
  }>;
}

export const useChatView = (options: UseChatViewOptions) => {
  const { mode, groupId } = options;
  const queryClient = useQueryClient();
  const { submitMessage } = useChat(mode);

  // 스트리밍 메시지 스토어 (재시도용)
  const { history: streamingHistory, removeMessage } = useTutorChatStore();

  // Warning 상태
  const [showWarning, setShowWarning] = useState(false);
  const [warningText, setWarningText] = useState("");

  // TTS 훅
  const { isSpeaking, currentMessageId, speak, stop, browserSupported } =
    useTextToSpeech({
      language: "ko-KR",
      onError: (error) => {
        console.error("TTS 에러:", error);
        setWarningText("음성 재생에 실패했습니다.");
        setShowWarning(true);
      },
    });

  // 그룹 리스트 조회 (복사 시 created_at 가져오기 위함)
  const { currentGroupId } = useGroupListStore();
  const { data: groupListData } = useInfiniteGroupListQuery({ mode });

  // Query
  const {
    data: chatHistory,
    isLoading,
    error,
  } = useChatHistoryQuery({ mode, groupId });

  /**
   * Warning 2초 후 자동 닫기
   */
  useEffect(() => {
    if (!showWarning) return;

    const timer = setTimeout(() => {
      setShowWarning(false);
    }, 2000);

    return () => {
      clearTimeout(timer);
    };
  }, [showWarning]);

  /**
   * Reference 데이터 변환 (Bubble 컴포넌트 형식)
   */
  const convertReferences = useCallback(
    (references: Reference[]): BubbleReference[] | undefined => {
      if (!references || references.length === 0) return undefined;

      return references.map((ref) => ({
        title: ref.name,
        source: ref.medium,
        url: ref.url
          ? `${ref.url.replace('/detail/', '/shareDetail/')}${ref.url.includes('?') ? '&' : '?'}referer=air`
          : ref.url,
        summary: ref.summary,
        images: ref.image?.map((img) => ({
          src: img.IMG_SRC,
          alt: img.IMG_Description || img.IMG_TITLE,
        })),
      }));
    },
    []
  );

  /**
   * 복사 핸들러
   */
  const handleCopy = useCallback(
    async (item: ChatMessage) => {
      try {
        const text = generateCopyText(item);
        await navigator.clipboard.writeText(text);

        // 복사 완료 알림 표시
        setWarningText("내용이 복사되었습니다.");
        setShowWarning(true);
      } catch (error) {
        console.error("복사 실패:", error);
        setWarningText("복사에 실패했습니다.");
        setShowWarning(true);
      }
    },
    [groupListData, currentGroupId]
  );

  /**
   * 복사용 텍스트 생성
   */
  const generateCopyText = (item: ChatMessage): string => {
    // 현재 그룹의 created_at 조회
    const currentGroup = groupListData?.pages
      .flatMap((page) => page.groups)
      .find((group) => group.id === currentGroupId);

    // 헤더: 질문 + 생성일시
    let text = `\n과학동아AiR. ${item.input_text}`;
    if (currentGroup?.created_at) {
      text += ` (${formatDateString(currentGroup.created_at)})`;
    }
    text += "\n\n";

    // AI 응답
    text += `${item.response}\n\n`;

    // 참고자료
    if (item.reference && item.reference.length > 0) {
      text += `[참고자료]\n`;
      item.reference.forEach((ref) => {
        // URL에서 년도/호수 추출 (예: C201208N011 -> 2012년 08호)
        const lastPart = ref.url.split("/").pop() || "";
        const datePart = lastPart.substring(1, 7); // "201208"
        const year = datePart.substring(0, 4); // "2012"
        const ho = datePart.substring(4, 6); // "08"

        // 포맷: medium - name - 년도/호수 - URL
        text += `${ref.medium} - ${ref.name} - ${year}년 ${ho}호 - ${ref.url}\n`;
      });
    }

    return text;
  };

  /**
   * 좋아요 핸들러
   */
  const handleLike = useCallback(
    async (item: ChatMessage) => {
      // 이미 좋아요를 누른 경우 무시
      if (item.user_eva?.simple_eva === 1) return;

      try {
        // 타입 변경 시 eva도 빈 문자열로 초기화
        await tutorApiService.submitReaction(item.response_id, 1, "");

        // 캐시 업데이트 - eva도 초기화
        queryClient.setQueryData<ChatMessage[]>(
          ["chat-history", mode, groupId],
          (old) => {
            if (!old) return old;
            return old.map((msg) =>
              msg.response_id === item.response_id
                ? {
                    ...msg,
                    user_eva: {
                      simple_eva: 1,
                      eva: "",
                    },
                  }
                : msg
            );
          }
        );
      } catch (error) {
        console.error("handleLike 에러:", error);
        setWarningText("좋아요 등록에 실패했습니다.");
        setShowWarning(true);
      }
    },
    [mode, groupId, queryClient]
  );

  /**
   * 싫어요 핸들러
   */
  const handleDislike = useCallback(
    async (item: ChatMessage) => {
      // 이미 싫어요를 누른 경우 무시
      if (item.user_eva?.simple_eva === 0) return;

      try {
        // 타입 변경 시 eva도 빈 문자열로 초기화
        await tutorApiService.submitReaction(item.response_id, 0, "");

        // 캐시 업데이트 - eva도 초기화
        queryClient.setQueryData<ChatMessage[]>(
          ["chat-history", mode, groupId],
          (old) => {
            if (!old) return old;
            return old.map((msg) =>
              msg.response_id === item.response_id
                ? {
                    ...msg,
                    user_eva: {
                      simple_eva: 0,
                      eva: "",
                    },
                  }
                : msg
            );
          }
        );
      } catch (error) {
        console.error("handleDislike 에러:", error);
        setWarningText("싫어요 등록에 실패했습니다.");
        setShowWarning(true);
      }
    },
    [mode, groupId, queryClient]
  );

  /**
   * 피드백 텍스트 제출 핸들러 (메시지별 커링)
   */
  const createFeedbackSubmitHandler = useCallback(
    (
      responseId: string,
      currentEva: { simple_eva: 0 | 1 | null; eva?: string }
    ) => {
      return async (feedback: string, type: "like" | "dislike") => {
        const simpleEva = type === "like" ? 1 : 0;

        try {
          // API 호출 (simple_eva + eva 함께 전송)
          await tutorApiService.submitReaction(responseId, simpleEva, feedback);

          // 캐시 업데이트
          queryClient.setQueryData<ChatMessage[]>(
            ["chat-history", mode, groupId],
            (old) => {
              if (!old) return old;
              return old.map((msg) =>
                msg.response_id === responseId
                  ? {
                      ...msg,
                      user_eva: {
                        simple_eva: simpleEva,
                        eva: feedback,
                      },
                    }
                  : msg
              );
            }
          );

          // 성공 알림
          setWarningText("소중한 의견 감사합니다.");
          setShowWarning(true);
        } catch (error) {
          console.error("handleFeedbackSubmit 에러:", error);
          setWarningText("피드백 등록에 실패했습니다.");
          setShowWarning(true);
        }
      };
    },
    [mode, groupId, queryClient]
  );

  /**
   * 재생성 핸들러
   *
   * 하이브리드 재생성 로직 (DELETE API 없음):
   * 1. Optimistic: 캐시에서 마지막 메시지 제거 → UI 즉시 업데이트
   * 2. 스트리밍: 새 질문으로 스트리밍 시작
   * 3. 완료 후 PATCH: 백엔드에 같은 response_id로 업데이트
   */
  const handleRegenerate = useCallback(
    (item: ChatMessage) => {
      try {
        // 1. Optimistic Update: 캐시에서 마지막 메시지 제거
        queryClient.setQueryData<ChatMessage[]>(
          ["chat-history", mode, groupId],
          (old) => {
            if (!old || old.length === 0) return old;
            // 마지막 메시지만 제거 (재생성 대상)
            return old.slice(0, -1);
          }
        );

        // 2. Regenerate 모드로 스트리밍 시작
        // - 같은 response_id로 PATCH 저장
        submitMessage(item.input_text, {
          isRegenerate: true,
          oldResponseId: item.response_id,
        });
      } catch (error) {
        console.error("재생성 실패:", error);
        setWarningText("재생성에 실패했습니다.");
        setShowWarning(true);
      }
    },
    [queryClient, submitMessage, mode, groupId]
  );

  /**
   * 스트리밍 메시지 재시도 핸들러
   * - 중단된 스트리밍 메시지를 삭제하고 다시 전송
   */
  const handleRegenerateStreaming = useCallback(
    (aiMessage: Message) => {
      // 1. 같은 group_id의 user 메시지 찾기
      const userMessage = streamingHistory.find(
        (msg) => msg.sender === "user" && msg.group_id === aiMessage.group_id
      );

      if (!userMessage) {
        console.warn("재시도할 사용자 메시지를 찾을 수 없습니다.");
        return;
      }

      // 2. 스트리밍 메시지 제거
      removeMessage(aiMessage.id);
      removeMessage(userMessage.id);

      // 3. 다시 전송
      submitMessage(userMessage.content);
    },
    [streamingHistory, removeMessage, submitMessage]
  );

  /**
   * TTS 핸들러
   */
  const handleTts = useCallback(
    (item: ChatMessage) => {
      // 브라우저 미지원 체크
      if (!browserSupported) {
        setWarningText("브라우저가 음성 재생을 지원하지 않습니다.");
        setShowWarning(true);
        return;
      }

      // 빈 텍스트 체크
      if (!item.response || item.response.trim() === "") {
        return;
      }

      // 마크다운 제거
      const cleanText = stripMarkdown(item.response);

      // 같은 메시지면 중단 (토글), 다른 메시지면 새로 재생
      if (currentMessageId === item.response_id) {
        stop(); // 같은 메시지: 토글(중단)
      } else {
        speak(cleanText, item.response_id); // 다른 메시지: 새로 재생 (내부에서 자동으로 이전 것 중단)
      }
    },
    [browserSupported, currentMessageId, stop, speak]
  );

  /**
   * 추천 질문 클릭 핸들러
   */
  const handleSuggestionClick = useCallback(
    (question: string) => {
      submitMessage(question);
    },
    [submitMessage]
  );

  /**
   * Bubble 컴포넌트용 간단한 참고자료 변환
   */
  const convertSimpleReferences = useCallback(
    (references: Reference[]): Array<{ number: number; title: string }> => {
      if (!references || references.length === 0) return [];

      return references.map((ref, index) => ({
        number: index + 1,
        title: ref.name,
      }));
    },
    []
  );

  /**
   * Bubble 컴포넌트용 데이터 변환
   */
  const messages = useMemo(() => {
    if (!chatHistory) return [];

    return chatHistory.map((item, index) => ({
      id: item.id,
      responseId: item.response_id,
      input: item.input_text,
      output: item.response,
      references: convertSimpleReferences(item.reference),
      detailedReferences: convertReferences(item.reference),
      suggestions: item.recommended_questions,
      isLastMessage: index === chatHistory.length - 1,
      userEva: item.user_eva,
      onFeedbackSubmit: createFeedbackSubmitHandler(
        item.response_id,
        item.user_eva
      ),
    }));
  }, [
    chatHistory,
    convertReferences,
    convertSimpleReferences,
    createFeedbackSubmitHandler,
  ]);

  return {
    // 데이터
    chatHistory: chatHistory || [],
    messages,

    // 상태
    isLoading,
    error,

    // Warning 상태
    showWarning,
    warningText,

    // TTS 상태
    isSpeaking,
    currentSpeakingMessageId: currentMessageId,
    stopTts: stop,

    // 핸들러
    handleCopy,
    handleLike,
    handleDislike,
    handleRegenerate,
    handleRegenerateStreaming,
    handleTts,
    handleSuggestionClick,

    // 유틸리티
    convertReferences,
  };
};
